import { getSessionStore } from "app/utils/sessionStore";
import { THEME } from "app/utils/types";
import { ShieldCheck } from "lucide-react";

interface PledgedAssetRow {
  id: string;
  type: string;
  institution: string;
  accountId: string;
  value: string;
  creditPercent: string;
  creditValue: string;
}

const MOCK_PLEDGED_ASSETS: PledgedAssetRow[] = [
  {
    id: "1",
    type: "Marketable Securities",
    institution: "Fidelity",
    accountId: "4456493",
    value: "$650,000",
    creditPercent: "70%",
    creditValue: "$350,000",
  },
  {
    id: "2",
    type: "Letter of Credit",
    institution: "JP Morgan",
    accountId: "N/A",
    value: "$650,000",
    creditPercent: "16%",
    creditValue: "$85,000",
  },
];

const COLUMNS: { key: keyof PledgedAssetRow; label: string; width: string }[] = [
  { key: "type", label: "Type", width: "22%" },
  { key: "institution", label: "Institution", width: "18%" },
  { key: "accountId", label: "Account #/ ID", width: "18%" },
  { key: "value", label: "Value", width: "14%" },
  { key: "creditPercent", label: "Credit %", width: "14%" },
  { key: "creditValue", label: "Credit Value", width: "14%" },
];

export type { PledgedAssetRow };

interface OutsidePledgedAssetsCardProps {
  assets?: PledgedAssetRow[];
}

const OutsidePledgedAssetsCard = ({ assets }: OutsidePledgedAssetsCardProps = {}) => {
  const isDark = getSessionStore("globalTheme") === THEME.DARK;
  const rows = assets ?? MOCK_PLEDGED_ASSETS;

  const cardBg = isDark ? "bg-[#2A2A3D]" : "bg-[#F5F5F5]";
  const thBg = isDark ? "bg-[#1E1E2E]" : "bg-white";
  const thText = isDark ? "text-gray-400" : "text-[#94A3B8]";
  const tdText = isDark ? "text-gray-100" : "text-[#0F172A]";
  const divider = isDark ? "!border-[#2E2E42]" : "!border-[#F1F5F9]";
  const iconColor = isDark ? "#93C5FD" : "#1E3A8A";
  const headerText = isDark ? "text-[#93C5FD]" : "text-[#1E3A8A]";

  return (
    <div className={`w-full rounded-2xl shadow-lg mt-4 ${cardBg}`}>
      {/* ── Header ── */}
      <p
        className={`text-[13px] font-semibold conthrax-font p-4 flex items-center gap-2 ${headerText}`}
      >
        <ShieldCheck size={17} strokeWidth={2} color={iconColor} />
        Outside Pledged Assets
      </p>

      {/* ── Table ── */}
      <div className="w-full overflow-x-auto">
        <table className="w-full border-collapse">
          <thead className="border-b-2 border-[#F5F5F5]">
            <tr className={thBg}>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={{ width: col.width }}
                  className={`px-4 py-3 text-left text-[14px] font-medium uppercase tracking-wider ${thText} border-b ${divider}`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.id} className={index < rows.length - 1 ? `border-b ${divider}` : ""}>
                {COLUMNS.map((col) => (
                  <td
                    key={col.key}
                    className={`px-4 py-4 text-[12px] ${tdText} ${isDark ? "2A2A3D" : "!bg-white"}`}
                  >
                    {row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OutsidePledgedAssetsCard;
